/** Export presets for Tier 2 Group F. A preset is a named target
 *  dimension pair the user can pick from the Export dialog's Preset
 *  dropdown — selecting one drives the Dimensions inputs and locks
 *  the aspect ratio to the preset's intrinsic ratio.
 *
 *  Built-in presets cover the common social-media surfaces; user-
 *  saved presets are a follow-up sub-task and will live in a
 *  separate per-user storage layer (not in this module).
 *
 *  Keep this module dependency-free — it's imported by the dialog
 *  component and by future preset-picker surfaces; pulling in
 *  Fabric or React here would force every caller into the same
 *  bundle. Plain data + plain helpers only. */

/** Stable id for a built-in preset. Strings (not enums) so they
 *  survive serialization to JSON when user-saved presets ship and
 *  need a discriminator against built-ins. */
export type BuiltInPresetId =
  | 'twitter-post'
  | 'twitter-header'
  | 'instagram-square'
  | 'instagram-story'
  | 'youtube-thumbnail'
  | 'facebook-cover'
  | 'linkedin-banner';

export interface ExportPreset {
  id: BuiltInPresetId;
  /** Display name shown in the dropdown. Stable — used as React key
   *  in tests too, so renaming requires test updates. */
  label: string;
  width: number;
  height: number;
}

/** Sentinel value used by the dialog's Preset dropdown when no
 *  built-in preset matches the current dimensions. Selecting
 *  "custom" doesn't change dimensions — it just frees them. */
export const CUSTOM_PRESET = 'custom' as const;
export type CustomPresetMarker = typeof CUSTOM_PRESET;

/** Built-in preset list. Order is the order shown in the dropdown.
 *  Dimensions match each platform's recommended upload size as of
 *  2026-05; they're targets, not contracts — platforms occasionally
 *  shift their recommended sizes and this list updates in lockstep. */
export const BUILT_IN_EXPORT_PRESETS: readonly ExportPreset[] = [
  { id: 'twitter-post', label: 'Twitter Post', width: 1200, height: 675 },
  { id: 'twitter-header', label: 'Twitter Header', width: 1500, height: 500 },
  { id: 'instagram-square', label: 'Instagram Square', width: 1080, height: 1080 },
  { id: 'instagram-story', label: 'Instagram Story', width: 1080, height: 1920 },
  { id: 'youtube-thumbnail', label: 'YouTube Thumbnail', width: 1280, height: 720 },
  { id: 'facebook-cover', label: 'Facebook Cover', width: 820, height: 312 },
  { id: 'linkedin-banner', label: 'LinkedIn Banner', width: 1584, height: 396 },
];

/** Lookup helper. Returns undefined for `'custom'` and for any
 *  unknown id (defensive — id comes from a controlled `<select>`
 *  but a future user-preset persistence layer could feed a stale
 *  id in). */
export function findBuiltInPreset(id: string): ExportPreset | undefined {
  return BUILT_IN_EXPORT_PRESETS.find((p) => p.id === id);
}

/** Reverse lookup: given a (width, height), find the built-in
 *  preset whose intrinsic dimensions match exactly, or undefined
 *  if no built-in matches. Used by the dialog to keep the Preset
 *  dropdown in sync when the user types dimensions manually that
 *  happen to land on a preset size. */
export function matchPresetByDimensions(width: number, height: number): ExportPreset | undefined {
  return BUILT_IN_EXPORT_PRESETS.find((p) => p.width === width && p.height === height);
}
